import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FileText, Plus, Save, Trash2, Edit, Download, Users, Calendar, Eye, X, AlertTriangle, Image as ImageIcon, ChevronDown, ChevronUp, Search, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { usePropiedad } from '../contexts/PropiedadContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contratos = () => {
  const { propiedadSeleccionada } = usePropiedad();
  const [searchParams, setSearchParams] = useSearchParams();
  const clienteFiltro = searchParams.get('cliente');
  const quillRefPlantilla = useRef(null);
  const quillRefContrato = useRef(null);

  // Configuración del editor Quill
  const quillModules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        'image': function() {
          const input = document.createElement('input');
          input.setAttribute('type', 'file');
          input.setAttribute('accept', 'image/*');
          input.click();

          input.onchange = async () => {
            const file = input.files[0];
            if (file) {
              const formData = new FormData();
              formData.append('file', file);

              try {
                const response = await axios.post(`${API}/upload-firma`, formData, {
                  headers: { 'Content-Type': 'multipart/form-data' }
                });
                
                const imageUrl = `${BACKEND_URL}${response.data.url}`;
                const quill = this.quill;
                const range = quill.getSelection(true);
                quill.insertEmbed(range.index, 'image', imageUrl);
                quill.setSelection(range.index + 1);
                toast.success('Imagen insertada. Haz doble clic sobre ella para ver opciones de tamaño.');
              } catch (error) {
                console.error('Error al subir imagen:', error);
                toast.error('Error al subir la imagen');
              }
            }
          };
        }
      }
    }
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'align',
    'link', 'image',
    'width'
  ];

  // Estados principales
  const [vistaActual, setVistaActual] = useState('plantillas'); // 'plantillas', 'contratos', 'generar'
  const [plantillas, setPlantillas] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [contratosFiltrados, setContratosFiltrados] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [canales, setCanales] = useState([]);
  const [propiedades, setPropiedades] = useState([]);
  const [variablesDisponibles, setVariablesDisponibles] = useState({
    variables_generales: [],
    variables_cliente: [],
    variables_reserva: [],
    variables_datos: {}
  });

  // Estado para mostrar/ocultar variables
  const [mostrarVariables, setMostrarVariables] = useState(false);

  // Estado para filtro de búsqueda de contratos
  const [filtroClienteTexto, setFiltroClienteTexto] = useState('');

  // Estados para modales y formularios
  const [modalPlantillaAbierto, setModalPlantillaAbierto] = useState(false);
  const [modalContratoAbierto, setModalContratoAbierto] = useState(false);
  const [modalVisualizarAbierto, setModalVisualizarAbierto] = useState(false);
  const [plantillaEditando, setPlantillaEditando] = useState(null);
  const [plantillaAEliminar, setPlantillaAEliminar] = useState(null);
  const [contratoAEliminar, setContratoAEliminar] = useState(null);
  const [contratoSeleccionado, setContratoSeleccionado] = useState(null);
  const [modalPDFPreviewAbierto, setModalPDFPreviewAbierto] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');

  // Estados para formulario de plantilla
  const [nombrePlantilla, setNombrePlantilla] = useState('');
  const [contenidoPlantilla, setContenidoPlantilla] = useState('');

  // Estados para generar contrato
  const [clienteSeleccionado, setClienteSeleccionado] = useState('');
  const [reservaSeleccionada, setReservaSeleccionada] = useState('');
  const [plantillaSeleccionada, setPlantillaSeleccionada] = useState('');
  const [contenidoContrato, setContenidoContrato] = useState('');
  const [generandoPDF, setGenerandoPDF] = useState(false);

  // Funciones de carga de datos
  const cargarDatos = async () => {
    if (!propiedadSeleccionada) return;
    try {
      const [resPlantillas, resContratos, resClientes, resVariables, resCanales, resPropiedades] = await Promise.all([
        axios.get(`${API}/plantillas-contrato`),
        axios.get(`${API}/contratos`),
        axios.get(`${API}/clientes?propiedad_id=${propiedadSeleccionada.id_propiedad}`),
        axios.get(`${API}/variables-contrato?propiedad_id=${propiedadSeleccionada?.id_propiedad}`),
        axios.get(`${API}/canales`),
        axios.get(`${API}/propiedades`)
      ]);
      
      setPlantillas(resPlantillas.data);
      setContratos(resContratos.data);
      setClientes(resClientes.data);
      setVariablesDisponibles(resVariables.data);
      setCanales(resCanales.data);
      setPropiedades(resPropiedades.data);
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error al cargar los datos');
    }
  };

  const cargarReservasCliente = async (clienteId) => {
    try {
      // Obtener el cliente seleccionado para buscar por nombre
      const cliente = clientes.find(c => c.id_cliente === clienteId);
      if (!cliente) {
        setReservas([]);
        return;
      }

      // Obtener todas las reservas y filtrar las del cliente
      const resReservas = await axios.get(`${API}/reservas?propiedad_id=${propiedadSeleccionada?.id_propiedad}`);
      const nombreCompleto = `${cliente.nombre} ${cliente.apellido || ''}`.trim().replace(/\s+/g, ' ');
      const nombreSolo = cliente.nombre?.trim();
      
      // Filtrar por cliente_id si existe, sino por nombre (flexible)
      const reservasCliente = resReservas.data.filter(r => {
        // Match by cliente_id
        if (r.cliente_id && r.cliente_id === clienteId) {
          return true;
        }
        
        // Normalize huesped_nombre for comparison
        const huespedNombre = (r.huesped_nombre || '').trim().replace(/\s+/g, ' ');
        
        // Match by full name
        if (huespedNombre === nombreCompleto) {
          return true;
        }
        
        // Match by first name only
        if (nombreSolo && huespedNombre === nombreSolo) {
          return true;
        }
        
        // Match if huesped_nombre contains the client's name
        if (nombreSolo && huespedNombre.toLowerCase().includes(nombreSolo.toLowerCase())) {
          return true;
        }
        
        return false;
      });
      
      setReservas(reservasCliente);
    } catch (error) {
      console.error('Error cargando reservas:', error);
      toast.error('Error al cargar reservas del cliente');
    }
  };

  useEffect(() => {
    if (propiedadSeleccionada) {
      cargarDatos();
    }
  }, [propiedadSeleccionada]);

  useEffect(() => {
    // Si hay un filtro de cliente en la URL, mostrar la vista de contratos
    if (clienteFiltro) {
      setVistaActual('contratos');
    }
  }, [clienteFiltro]);

  useEffect(() => {
    // Filtrar contratos por texto de búsqueda (coincidencia en nombre del cliente)
    if (filtroClienteTexto.trim()) {
      const textoLower = filtroClienteTexto.toLowerCase().trim();
      const filtrados = contratos.filter(c => 
        (c.nombre_cliente || '').toLowerCase().includes(textoLower)
      );
      setContratosFiltrados(filtrados);
    } else if (clienteFiltro) {
      // Filtro por ID de cliente (desde URL)
      const filtrados = contratos.filter(c => c.cliente_id === clienteFiltro);
      setContratosFiltrados(filtrados);
    } else {
      setContratosFiltrados(contratos);
    }
  }, [contratos, clienteFiltro, filtroClienteTexto]);

  useEffect(() => {
    // Cargar reservas cuando se selecciona un cliente
    if (clienteSeleccionado && clientes.length > 0) {
      cargarReservasCliente(clienteSeleccionado);
    }
  }, [clienteSeleccionado, clientes]);

  useEffect(() => {
    // Si viene desde reservas con parámetros para generar contrato
    const reservaParam = searchParams.get('reserva');
    const clienteParam = searchParams.get('cliente');
    const generarParam = searchParams.get('generar');

    if (reservaParam && clienteParam && generarParam === 'true' && clientes.length > 0) {
      setClienteSeleccionado(clienteParam);
      setReservaSeleccionada(reservaParam);
      setVistaActual('generar');
      
      // Limpiar parámetros de la URL para evitar que se vuelva a ejecutar
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('reserva');
      newSearchParams.delete('cliente');
      newSearchParams.delete('generar');
      window.history.replaceState({}, '', `${window.location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`);
    }
  }, [searchParams, clientes]);

  // Efecto para hacer imágenes redimensionables con prompt
  useEffect(() => {
    const handleImageClick = (e) => {
      if (e.target.tagName === 'IMG' && e.target.closest('.ql-editor')) {
        const img = e.target;
        const currentWidth = img.width || img.naturalWidth;
        
        const newWidth = prompt(
          'Ingresa el ancho de la imagen en píxeles (deja vacío para restablecer al tamaño original):',
          currentWidth
        );
        
        if (newWidth === null) return; // Usuario canceló
        
        if (newWidth === '' || newWidth === '0') {
          // Restablecer tamaño original
          img.removeAttribute('width');
          img.removeAttribute('height');
          img.style.width = '';
          img.style.height = '';
        } else {
          const width = parseInt(newWidth);
          if (!isNaN(width) && width > 0) {
            img.setAttribute('width', width);
            img.style.width = `${width}px`;
            img.style.height = 'auto';
          }
        }
      }
    };

    document.addEventListener('dblclick', handleImageClick);
    return () => document.removeEventListener('dblclick', handleImageClick);
  }, []);

  // ==================== FUNCIONES AUXILIARES PARA EDITOR ====================

  const insertarTablaEnEditor = (filas = 3, columnas = 3, isPlantilla = true) => {
    const quillRef = isPlantilla ? quillRefPlantilla : quillRefContrato;
    if (!quillRef.current) return;

    const editor = quillRef.current.getEditor();
    const range = editor.getSelection(true);
    
    // Crear tabla HTML
    let tableHTML = '<table border="1" style="border-collapse: collapse; width: 100%;"><tbody>';
    for (let i = 0; i < filas; i++) {
      tableHTML += '<tr>';
      for (let j = 0; j < columnas; j++) {
        tableHTML += '<td style="border: 1px solid #000; padding: 8px;">Celda</td>';
      }
      tableHTML += '</tr>';
    }
    tableHTML += '</tbody></table><p><br></p>';
    
    editor.clipboard.dangerouslyPasteHTML(range.index, tableHTML);
    editor.setSelection(range.index + 1);
    toast.success('Tabla insertada correctamente');
  };

  const insertarVariableEnEditor = (variable, isPlantilla = true) => {
    const quillRef = isPlantilla ? quillRefPlantilla : quillRefContrato;
    if (!quillRef.current) return;

    const editor = quillRef.current.getEditor();
    const range = editor.getSelection(true);
    
    // La variable ya viene con llaves del backend, insertarla tal cual
    const varText = variable.startsWith('{') ? variable : `{${variable}}`;
    
    if (range) {
      editor.insertText(range.index, varText);
      editor.setSelection(range.index + varText.length);
    } else {
      const length = editor.getLength();
      editor.insertText(length, varText);
    }
  };

  // ==================== GESTIÓN DE PLANTILLAS ====================

  const abrirModalNuevaPlantilla = () => {
    setPlantillaEditando(null);
    setNombrePlantilla('');
    setContenidoPlantilla('');
    setModalPlantillaAbierto(true);
  };

  const abrirModalEditarPlantilla = (plantilla) => {
    setPlantillaEditando(plantilla);
    setNombrePlantilla(plantilla.nombre);
    setContenidoPlantilla(plantilla.contenido);
    setModalPlantillaAbierto(true);
  };

  const insertarVariableEnPlantilla = (variable) => {
    insertarVariableEnEditor(variable, true);
  };

  const eliminarVariableDato = async (key) => {
    const variable = variablesDisponibles.variables_datos[key];
    if (!variable || !variable.id_dato) {
      toast.error('No se puede eliminar esta variable');
      return;
    }
    
    if (window.confirm(`¿Estás seguro de eliminar la variable "{${key}}"? Esto eliminará el dato permanentemente.`)) {
      try {
        await axios.delete(`${API}/datos/${variable.id_dato}`);
        toast.success(`Variable "{${key}}" eliminada`);
        // Actualizar la lista de variables
        const nuevasVariables = { ...variablesDisponibles.variables_datos };
        delete nuevasVariables[key];
        setVariablesDisponibles({
          ...variablesDisponibles,
          variables_datos: nuevasVariables
        });
      } catch (error) {
        toast.error('Error al eliminar la variable');
        console.error(error);
      }
    }
  };

  const guardarPlantilla = async () => {
    if (!nombrePlantilla.trim() || !contenidoPlantilla.trim()) {
      toast.error('Complete todos los campos');
      return;
    }

    try {
      if (plantillaEditando) {
        // Editar plantilla existente
        await axios.put(`${API}/plantillas-contrato/${plantillaEditando.id_plantilla}`, {
          nombre: nombrePlantilla,
          contenido: contenidoPlantilla
        });
        toast.success('Plantilla actualizada');
      } else {
        // Crear nueva plantilla
        await axios.post(`${API}/plantillas-contrato`, {
          nombre: nombrePlantilla,
          contenido: contenidoPlantilla
        });
        toast.success('Plantilla creada');
      }
      
      setModalPlantillaAbierto(false);
      cargarDatos();
    } catch (error) {
      console.error('Error guardando plantilla:', error);
      toast.error('Error al guardar la plantilla');
    }
  };

  const confirmarEliminarPlantilla = async () => {
    if (!plantillaAEliminar) return;

    try {
      await axios.delete(`${API}/plantillas-contrato/${plantillaAEliminar.id_plantilla}`);
      toast.success('Plantilla eliminada');
      setPlantillaAEliminar(null);
      cargarDatos();
    } catch (error) {
      console.error('Error eliminando plantilla:', error);
      toast.error('Error al eliminar la plantilla');
    }
  };

  // ==================== GENERACIÓN DE CONTRATOS ====================

  const iniciarGeneracionContrato = () => {
    setClienteSeleccionado('');
    setReservaSeleccionada('');
    setPlantillaSeleccionada('');
    setContenidoContrato('');
    setReservas([]);
    setVistaActual('generar');
  };

  const formatearMonto = (valor) => {
    if (!valor) return '';
    const formatted = Math.round(valor).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `$${formatted}`;
  };

  // Función para sustituir variables en el contenido
  const sustituirVariablesEnContenido = async (contenido) => {
    if (!clienteSeleccionado || !reservaSeleccionada) {
      return contenido;
    }

    try {
      // Obtener los datos del cliente y la reserva
      const [resCliente, resReserva] = await Promise.all([
        axios.get(`${API}/clientes/${clienteSeleccionado}`),
        axios.get(`${API}/reservas/${reservaSeleccionada}`)
      ]);

      const cliente = resCliente.data;
      const reserva = resReserva.data;

      // Variables generales
      const fechaHoy = new Date().toLocaleDateString('es-CL', { 
        day: '2-digit', 
        month: 'long', 
        year: 'numeric' 
      });
      contenido = contenido.replace(/{fecha_hoy}/gi, fechaHoy);
      contenido = contenido.replace(/{FECHA_HOY}/g, fechaHoy);
      
      // Propiedad
      const propiedad = propiedades.find(p => p.id_propiedad === reserva.propiedad_id);
      contenido = contenido.replace(/{PROPIEDAD_NOMBRE}/g, propiedad?.nombre || '');
      contenido = contenido.replace(/{PROPIEDAD_DIRECCION}/g, propiedad?.direccion || '');

      // Variables del cliente
      const nombreCompleto = `${cliente.nombre || ''} ${cliente.apellido || ''}`.trim();
      contenido = contenido.replace(/{nombre}/gi, cliente.nombre || '');
      contenido = contenido.replace(/{CLIENTE_NOMBRE}/g, cliente.nombre || '');
      contenido = contenido.replace(/{apellido}/gi, cliente.apellido || '');
      contenido = contenido.replace(/{CLIENTE_APELLIDO}/g, cliente.apellido || '');
      contenido = contenido.replace(/{CLIENTE_NOMBRE_COMPLETO}/g, nombreCompleto);
      contenido = contenido.replace(/{telefono}/gi, cliente.telefono || '');
      contenido = contenido.replace(/{CLIENTE_TELEFONO}/g, cliente.telefono || '');
      contenido = contenido.replace(/{email}/gi, cliente.email || '');
      contenido = contenido.replace(/{CLIENTE_EMAIL}/g, cliente.email || '');
      contenido = contenido.replace(/{rut}/gi, cliente.rut || '');
      contenido = contenido.replace(/{CLIENTE_RUT}/g, cliente.rut || '');
      contenido = contenido.replace(/{direccion}/gi, cliente.direccion || '');
      contenido = contenido.replace(/{CLIENTE_DIRECCION}/g, cliente.direccion || '');
      contenido = contenido.replace(/{CLIENTE_NACIONALIDAD}/g, cliente.nacionalidad || '');
      contenido = contenido.replace(/{CLIENTE_NOTAS}/g, cliente.notas || '');

      // Variables de la reserva
      const formatDate = (dateString) => {
        if (!dateString) return '';
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
      };
      
      const fechaIngreso = formatDate(reserva.fecha_check_in);
      const fechaSalida = formatDate(reserva.fecha_check_out);
      
      // Obtener gastos asociados
      let gastosAsociados = 0;
      try {
        const resGastos = await axios.get(`${API}/gastos/reserva/${reserva.id_reserva}`);
        gastosAsociados = resGastos.data.reduce((sum, g) => sum + (parseFloat(g.monto) || 0), 0);
      } catch (e) {
        console.log('No hay gastos asociados para esta reserva');
      }
      
      // Calcular valores
      const valorNoche = reserva.valor_noche || 0;
      const noches = reserva.noches || 0;
      const subTotal = reserva.monto_bruto || (noches * valorNoche) || 0;
      const montoTotal = subTotal + gastosAsociados;
      const comisiones = reserva.comisiones || 0;
      const abono = reserva.abono || 0;
      const saldo = subTotal - abono;
      
      contenido = contenido.replace(/{check_in}/gi, fechaIngreso);
      contenido = contenido.replace(/{FECHA_CHECK_IN}/g, fechaIngreso);
      contenido = contenido.replace(/{check_out}/gi, fechaSalida);
      contenido = contenido.replace(/{FECHA_CHECK_OUT}/g, fechaSalida);
      contenido = contenido.replace(/{noches}/gi, noches.toString());
      contenido = contenido.replace(/{NOCHES}/g, noches.toString());
      contenido = contenido.replace(/{valor_noche}/gi, formatearMonto(valorNoche));
      contenido = contenido.replace(/{VALOR_NOCHE}/g, formatearMonto(valorNoche));
      contenido = contenido.replace(/{monto_bruto}/gi, formatearMonto(subTotal));
      contenido = contenido.replace(/{SUB_TOTAL}/g, formatearMonto(subTotal));
      contenido = contenido.replace(/{gastos_asociados}/gi, formatearMonto(gastosAsociados));
      contenido = contenido.replace(/{GASTOS_ASOCIADOS}/g, formatearMonto(gastosAsociados));
      contenido = contenido.replace(/{monto_total}/gi, formatearMonto(montoTotal));
      contenido = contenido.replace(/{MONTO_TOTAL}/g, formatearMonto(montoTotal));
      contenido = contenido.replace(/{comisiones}/gi, formatearMonto(comisiones));
      contenido = contenido.replace(/{COMISIONES}/g, formatearMonto(comisiones));
      contenido = contenido.replace(/{abono}/gi, formatearMonto(abono));
      contenido = contenido.replace(/{ABONO}/g, formatearMonto(abono));
      contenido = contenido.replace(/{saldo}/gi, formatearMonto(saldo));
      contenido = contenido.replace(/{SALDO}/g, formatearMonto(saldo));
      contenido = contenido.replace(/{estado_reserva}/gi, reserva.estado || '');
      contenido = contenido.replace(/{ESTADO_RESERVA}/g, reserva.estado || '');
      contenido = contenido.replace(/{canal}/gi, reserva.canal || '');
      contenido = contenido.replace(/{CANAL}/g, reserva.canal || '');

      // Variables de datos personalizados
      if (variablesDisponibles.variables_datos) {
        Object.entries(variablesDisponibles.variables_datos).forEach(([key, value]) => {
          const regex = new RegExp(`\\{${key}\\}`, 'gi');
          contenido = contenido.replace(regex, value || '');
        });
      }

      return contenido;
    } catch (error) {
      console.error('Error sustituyendo variables:', error);
      return contenido;
    }
  };

  const cargarPlantillaEnContrato = async () => {
    if (!plantillaSeleccionada || !clienteSeleccionado || !reservaSeleccionada) {
      toast.error('Seleccione cliente, reserva y plantilla');
      return;
    }

    try {
      // Obtener los datos del cliente y la reserva
      const [resCliente, resReserva, resPlantilla] = await Promise.all([
        axios.get(`${API}/clientes/${clienteSeleccionado}`),
        axios.get(`${API}/reservas/${reservaSeleccionada}`),
        axios.get(`${API}/plantillas-contrato/${plantillaSeleccionada}`)
      ]);

      const cliente = resCliente.data;
      const reserva = resReserva.data;
      const plantilla = resPlantilla.data;

      // Sustituir variables en el contenido de la plantilla
      let contenido = plantilla.contenido;

      // Variables generales
      const fechaHoy = new Date().toLocaleDateString('es-CL', { 
        day: '2-digit', 
        month: 'long', 
        year: 'numeric' 
      });
      contenido = contenido.replace(/{fecha_hoy}/gi, fechaHoy);
      contenido = contenido.replace(/{FECHA_HOY}/g, fechaHoy);
      
      // Propiedad
      const propiedad = propiedades.find(p => p.id_propiedad === reserva.propiedad_id);
      contenido = contenido.replace(/{PROPIEDAD_NOMBRE}/g, propiedad?.nombre || '');
      contenido = contenido.replace(/{PROPIEDAD_DIRECCION}/g, propiedad?.direccion || '');

      // Variables del cliente (minúsculas y mayúsculas)
      const nombreCompleto = `${cliente.nombre || ''} ${cliente.apellido || ''}`.trim();
      contenido = contenido.replace(/{nombre}/gi, cliente.nombre || '');
      contenido = contenido.replace(/{CLIENTE_NOMBRE}/g, cliente.nombre || '');
      contenido = contenido.replace(/{apellido}/gi, cliente.apellido || '');
      contenido = contenido.replace(/{CLIENTE_APELLIDO}/g, cliente.apellido || '');
      contenido = contenido.replace(/{CLIENTE_NOMBRE_COMPLETO}/g, nombreCompleto);
      contenido = contenido.replace(/{telefono}/gi, cliente.telefono || '');
      contenido = contenido.replace(/{CLIENTE_TELEFONO}/g, cliente.telefono || '');
      contenido = contenido.replace(/{email}/gi, cliente.email || '');
      contenido = contenido.replace(/{CLIENTE_EMAIL}/g, cliente.email || '');
      contenido = contenido.replace(/{rut}/gi, cliente.rut || '');
      contenido = contenido.replace(/{CLIENTE_RUT}/g, cliente.rut || '');
      contenido = contenido.replace(/{direccion}/gi, cliente.direccion || '');
      contenido = contenido.replace(/{CLIENTE_DIRECCION}/g, cliente.direccion || '');
      contenido = contenido.replace(/{CLIENTE_NACIONALIDAD}/g, cliente.nacionalidad || '');
      contenido = contenido.replace(/{CLIENTE_NOTAS}/g, cliente.notas || '');

      // Variables de la reserva
      // Formatear fechas a DD/MM/YYYY sin conversión de timezone
      const formatDate = (dateString) => {
        if (!dateString) return '';
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
      };
      
      const fechaIngreso = formatDate(reserva.fecha_check_in);
      const fechaSalida = formatDate(reserva.fecha_check_out);
      
      // Obtener gastos asociados a la reserva
      let gastosAsociados = 0;
      try {
        const resGastos = await axios.get(`${API}/gastos/reserva/${reserva.id_reserva}`);
        gastosAsociados = resGastos.data.reduce((sum, g) => sum + (parseFloat(g.monto) || 0), 0);
      } catch (e) {
        console.log('No hay gastos asociados para esta reserva');
      }
      
      // Calcular valores con la nueva lógica:
      // SUB_TOTAL = noches × valor_noche (guardado en monto_bruto)
      // MONTO_TOTAL = SUB_TOTAL + gastos_asociados
      const valorNoche = reserva.valor_noche || 0;
      const noches = reserva.noches || 0;
      const subTotal = reserva.monto_bruto || (noches * valorNoche) || 0;
      const montoTotal = subTotal + gastosAsociados;
      const comisiones = reserva.comisiones || 0;
      const montoNeto = subTotal - comisiones;
      const abono = Math.round(montoTotal * 0.5);
      const saldo = Math.round(montoTotal * 0.5);
      
      // Reemplazos de reserva (minúsculas - legacy/compatibilidad)
      contenido = contenido.replace(/{fecha_ingreso}/gi, fechaIngreso);
      contenido = contenido.replace(/{fecha_salida}/gi, fechaSalida);
      contenido = contenido.replace(/{fecha_check_in}/gi, fechaIngreso);
      contenido = contenido.replace(/{fecha_check_out}/gi, fechaSalida);
      contenido = contenido.replace(/{noches}/gi, noches.toString());
      contenido = contenido.replace(/{valor_por_noche}/gi, formatearMonto(valorNoche));
      contenido = contenido.replace(/{valor_noche}/gi, formatearMonto(valorNoche));
      contenido = contenido.replace(/{subtotal}/gi, formatearMonto(subTotal));
      contenido = contenido.replace(/{sub_total}/gi, formatearMonto(subTotal));
      contenido = contenido.replace(/{gastos_asociados}/gi, formatearMonto(gastosAsociados));
      contenido = contenido.replace(/{monto_total}/gi, formatearMonto(montoTotal));
      contenido = contenido.replace(/{comisiones}/gi, formatearMonto(comisiones));
      contenido = contenido.replace(/{abono}/gi, formatearMonto(abono));
      contenido = contenido.replace(/{saldo}/gi, formatearMonto(saldo));
      
      // Reemplazos de reserva (MAYÚSCULAS - formato oficial)
      contenido = contenido.replace(/{FECHA_CHECK_IN}/g, fechaIngreso);
      contenido = contenido.replace(/{FECHA_CHECK_OUT}/g, fechaSalida);
      contenido = contenido.replace(/{NOCHES}/g, noches.toString());
      contenido = contenido.replace(/{VALOR_NOCHE}/g, formatearMonto(valorNoche));
      contenido = contenido.replace(/{SUB_TOTAL}/g, formatearMonto(subTotal));
      contenido = contenido.replace(/{GASTOS_ASOCIADOS}/g, formatearMonto(gastosAsociados));
      contenido = contenido.replace(/{MONTO_TOTAL}/g, formatearMonto(montoTotal));
      contenido = contenido.replace(/{COMISIONES}/g, formatearMonto(comisiones));
      contenido = contenido.replace(/{ABONO}/g, formatearMonto(abono));
      contenido = contenido.replace(/{SALDO}/g, formatearMonto(saldo));
      contenido = contenido.replace(/{ESTADO_RESERVA}/g, reserva.estado_reserva || '');
      contenido = contenido.replace(/{CANAL}/g, canales.find(c => c.id_canal === reserva.canal_id)?.nombre || '');

      // Variables de datos (dinámicas)
      const datos = variablesDisponibles.variables_datos || {};
      for (const [key, value] of Object.entries(datos)) {
        const pattern = new RegExp(`{${key}}`, 'gi');
        contenido = contenido.replace(pattern, value.dato || '');
      }

      setContenidoContrato(contenido);
      toast.success('Plantilla cargada y variables sustituidas');
    } catch (error) {
      console.error('Error cargando plantilla:', error);
      toast.error('Error al cargar la plantilla');
    }
  };

  const generarContrato = async () => {
    // Solo requerimos cliente, reserva y contenido (plantilla es opcional)
    if (!clienteSeleccionado || !reservaSeleccionada || !contenidoContrato.trim()) {
      toast.error('Debe seleccionar cliente, reserva y agregar contenido al contrato');
      return;
    }

    try {
      // Sustituir variables antes de guardar el contrato
      const contenidoFinal = await sustituirVariablesEnContenido(contenidoContrato);
      
      // Crear el contrato
      const response = await axios.post(`${API}/contratos`, {
        cliente_id: clienteSeleccionado,
        reserva_id: reservaSeleccionada,
        plantilla_id: plantillaSeleccionada || null,
        contenido_completo: contenidoFinal
      });

      const contratoCreado = response.data;
      toast.success('Contrato generado exitosamente');

      // Generar el PDF automáticamente
      setGenerandoPDF(true);
      try {
        const pdfResponse = await axios.post(`${API}/contratos/${contratoCreado.id_contrato}/generar-pdf`);
        toast.success('PDF generado exitosamente');
        
        // Abrir vista previa del PDF automáticamente
        if (pdfResponse.data.pdf_url) {
          // Obtener el contrato actualizado con la URL del PDF
          const contratoActualizado = {
            ...contratoCreado,
            pdf_url: pdfResponse.data.pdf_url,
            nombre_cliente: contratoCreado.nombre_cliente || `${clientes.find(c => c.id_cliente === clienteSeleccionado)?.nombre || ''} ${clientes.find(c => c.id_cliente === clienteSeleccionado)?.apellido || ''}`.trim()
          };
          
          setPdfPreviewUrl(`${BACKEND_URL}${pdfResponse.data.pdf_url}`);
          setContratoSeleccionado(contratoActualizado);
          setModalPDFPreviewAbierto(true);
        }
      } catch (pdfError) {
        console.error('Error generando PDF:', pdfError);
        toast.warning('Contrato creado, pero error al generar PDF');
      }
      setGenerandoPDF(false);

      // Volver a la vista de contratos
      setVistaActual('contratos');
      cargarDatos();
    } catch (error) {
      console.error('Error generando contrato:', error);
      toast.error('Error al generar el contrato');
      setGenerandoPDF(false);
    }
  };

  // ==================== GESTIÓN DE CONTRATOS ====================

  const descargarPDF = async (contrato) => {
    if (!contrato.pdf_url) {
      toast.error('PDF no generado. Genera el PDF primero.');
      return;
    }

    // Open PDF preview modal instead of direct download
    setPdfPreviewUrl(`${BACKEND_URL}${contrato.pdf_url}`);
    setContratoSeleccionado(contrato);
    setModalPDFPreviewAbierto(true);
  };

  const descargarPDFDirecto = () => {
    if (pdfPreviewUrl) {
      // Create a temporary link to force download
      const link = document.createElement('a');
      link.href = pdfPreviewUrl;
      link.download = `contrato_${contratoSeleccionado?.nombre_cliente || 'documento'}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Descargando PDF...');
    }
  };

  const regenerarPDF = async (contratoId) => {
    try {
      setGenerandoPDF(true);
      await axios.post(`${API}/contratos/${contratoId}/generar-pdf`);
      toast.success('PDF regenerado exitosamente');
      setGenerandoPDF(false);
      cargarDatos();
    } catch (error) {
      console.error('Error regenerando PDF:', error);
      toast.error('Error al regenerar el PDF');
      setGenerandoPDF(false);
    }
  };

  const confirmarEliminarContrato = async () => {
    if (!contratoAEliminar) return;

    try {
      await axios.delete(`${API}/contratos/${contratoAEliminar.id_contrato}`);
      toast.success('Contrato eliminado');
      setContratoAEliminar(null);
      cargarDatos();
    } catch (error) {
      console.error('Error eliminando contrato:', error);
      toast.error('Error al eliminar el contrato');
    }
  };

  const visualizarContrato = (contrato) => {
    setContratoSeleccionado(contrato);
    setModalVisualizarAbierto(true);
  };

  // ==================== INSERCIÓN DE VARIABLES ====================

  const insertarVariable = (variable) => {
    insertarVariableEnEditor(variable, false);
  };

  // ==================== RENDERIZADO ====================

  return (
    <div className="p-8 bg-gradient-to-br from-cyan-50 via-sky-50 to-blue-50 min-h-screen">
      {/* Estilos globales para redimensionar imágenes */}
      <style>{`
        .ql-editor img {
          cursor: pointer;
          max-width: 100%;
          height: auto;
        }
        .ql-editor img:hover {
          outline: 2px dashed #0891b2;
        }
        .ql-editor img.resizable {
          resize: both;
          overflow: auto;
        }
        .ql-editor img[style*="width"] {
          max-width: 100%;
        }
      `}</style>
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-cyan-800 mb-2 flex items-center gap-3">
            <FileText className="w-10 h-10" />
            Gestión de Contratos
          </h1>
          <p className="text-cyan-600">Crea plantillas y genera contratos personalizados para tus clientes</p>
        </div>

        {/* Navegación entre vistas */}
        <div className="mb-6 flex gap-4">
          <Button
            variant={vistaActual === 'plantillas' ? 'default' : 'outline'}
            onClick={() => setVistaActual('plantillas')}
            className={vistaActual === 'plantillas' ? 'bg-cyan-600 hover:bg-cyan-700' : ''}
          >
            <FileText className="w-4 h-4 mr-2" />
            Plantillas
          </Button>
          <Button
            variant={vistaActual === 'contratos' ? 'default' : 'outline'}
            onClick={() => setVistaActual('contratos')}
            className={vistaActual === 'contratos' ? 'bg-cyan-600 hover:bg-cyan-700' : ''}
          >
            <Users className="w-4 h-4 mr-2" />
            Contratos Generados
          </Button>
          <Button
            variant={vistaActual === 'generar' ? 'default' : 'outline'}
            onClick={iniciarGeneracionContrato}
            className={vistaActual === 'generar' ? 'bg-cyan-600 hover:bg-cyan-700' : ''}
          >
            <Plus className="w-4 h-4 mr-2" />
            Generar Nuevo Contrato
          </Button>
        </div>

        {/* Vista: Plantillas */}
        {vistaActual === 'plantillas' && (
          <div>
            <div className="mb-6 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-cyan-800">Plantillas de Contratos</h2>
              <Button onClick={abrirModalNuevaPlantilla} className="bg-cyan-600 hover:bg-cyan-700">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Plantilla
              </Button>
            </div>

            {plantillas.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">No hay plantillas creadas</p>
                <Button onClick={abrirModalNuevaPlantilla} className="mt-4 bg-cyan-600 hover:bg-cyan-700">
                  Crear primera plantilla
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {plantillas.map((plantilla) => (
                  <div key={plantilla.id_plantilla} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-cyan-800">{plantilla.nombre}</h3>
                        <p className="text-sm text-gray-500">
                          Creada: {new Date(plantilla.fecha_creacion).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => abrirModalEditarPlantilla(plantilla)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPlantillaAEliminar(plantilla)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 line-clamp-3 bg-gray-50 p-3 rounded">
                      {plantilla.contenido}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Vista: Contratos Generados */}
        {vistaActual === 'contratos' && (
          <div>
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="text-2xl font-bold text-cyan-800">
                Contratos Generados
              </h2>
              
              {/* Filtro por cliente - Campo de búsqueda */}
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Buscar por cliente..."
                    value={filtroClienteTexto}
                    onChange={(e) => setFiltroClienteTexto(e.target.value)}
                    className="pl-9 w-[250px]"
                  />
                  {filtroClienteTexto && (
                    <button
                      onClick={() => setFiltroClienteTexto('')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {contratosFiltrados.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <Users className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">
                  {filtroClienteTexto ? 'No se encontraron contratos con ese nombre' : 'No hay contratos generados'}
                </p>
                <Button onClick={iniciarGeneracionContrato} className="mt-4 bg-cyan-600 hover:bg-cyan-700">
                  Generar primer contrato
                </Button>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-cyan-600">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Fecha Generación
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Estado PDF
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {contratosFiltrados.map((contrato) => (
                      <tr key={contrato.id_contrato} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{contrato.nombre_cliente}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {contrato.fecha_generacion 
                              ? new Date(contrato.fecha_generacion).toLocaleString('es-CL', {
                                  day: '2-digit',
                                  month: '2-digit', 
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : 'Sin fecha'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {contrato.pdf_url ? (
                            <span className="px-2 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full">
                              Generado
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-semibold text-yellow-800 bg-yellow-100 rounded-full">
                              Pendiente
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex gap-2 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => visualizarContrato(contrato)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {contrato.pdf_url && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => descargarPDF(contrato)}
                                title="Ver PDF"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => regenerarPDF(contrato.id_contrato)}
                              disabled={generandoPDF}
                              title="Regenerar PDF"
                            >
                              <RefreshCw className={`w-4 h-4 ${generandoPDF ? 'animate-spin' : ''}`} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setContratoAEliminar(contrato)}
                              title="Eliminar contrato"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Vista: Generar Nuevo Contrato */}
        {vistaActual === 'generar' && (
          <div className="bg-white rounded-lg shadow p-8">
            <h2 className="text-2xl font-bold text-cyan-800 mb-6">Generar Nuevo Contrato</h2>

            <div className="space-y-6">
              {/* Paso 1: Seleccionar Cliente */}
              <div>
                <Label htmlFor="cliente" className="text-base font-semibold text-cyan-800 mb-2 block">
                  1. Seleccionar Cliente
                </Label>
                <Select value={clienteSeleccionado} onValueChange={setClienteSeleccionado}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione un cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientes.map((cliente) => (
                      <SelectItem key={cliente.id_cliente} value={cliente.id_cliente}>
                        {cliente.nombre} {cliente.apellido}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Paso 2: Seleccionar Reserva */}
              <div>
                <Label htmlFor="reserva" className="text-base font-semibold text-cyan-800 mb-2 block">
                  2. Seleccionar Reserva del Cliente
                </Label>
                <Select 
                  value={reservaSeleccionada} 
                  onValueChange={setReservaSeleccionada}
                  disabled={!clienteSeleccionado || reservas.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={
                      !clienteSeleccionado 
                        ? "Primero seleccione un cliente" 
                        : reservas.length === 0 
                        ? "Este cliente no tiene reservas" 
                        : "Seleccione una reserva"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {reservas.map((reserva) => (
                      <SelectItem key={reserva.id_reserva} value={reserva.id_reserva}>
                        {reserva.fecha_check_in} - {reserva.fecha_check_out} ({reserva.noches} noches)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Paso 3: Seleccionar Plantilla (Opcional) */}
              <div>
                <Label htmlFor="plantilla" className="text-base font-semibold text-cyan-800 mb-2 block">
                  3. Seleccionar Plantilla <span className="text-sm font-normal text-gray-500">(opcional)</span>
                </Label>
                <div className="flex gap-2">
                  <Select 
                    value={plantillaSeleccionada} 
                    onValueChange={setPlantillaSeleccionada}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccione una plantilla o escriba directamente" />
                    </SelectTrigger>
                    <SelectContent>
                      {plantillas.map((plantilla) => (
                        <SelectItem key={plantilla.id_plantilla} value={plantilla.id_plantilla}>
                          {plantilla.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    onClick={cargarPlantillaEnContrato}
                    disabled={!plantillaSeleccionada || !clienteSeleccionado || !reservaSeleccionada}
                    className="bg-cyan-600 hover:bg-cyan-700"
                  >
                    Cargar Plantilla
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Puede cargar una plantilla o escribir el contenido directamente en el editor
                </p>
              </div>

              {/* Variables disponibles - Colapsable */}
              {(clienteSeleccionado && reservaSeleccionada) && (
                <div className="bg-cyan-50 border border-cyan-200 rounded">
                  <button
                    onClick={() => setMostrarVariables(!mostrarVariables)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-cyan-100 transition-colors rounded"
                  >
                    <h3 className="font-semibold text-cyan-800">Variables disponibles</h3>
                    {mostrarVariables ? (
                      <ChevronUp className="w-5 h-5 text-cyan-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-cyan-600" />
                    )}
                  </button>
                  
                  {mostrarVariables && (
                    <div className="px-4 pb-4 grid grid-cols-2 md:grid-cols-4 gap-2 border-t border-cyan-200 pt-3">
                      {variablesDisponibles.variables_generales && (
                        <div>
                          <p className="text-xs font-semibold text-cyan-700 mb-1">Generales:</p>
                          {variablesDisponibles.variables_generales.map((v) => (
                            <button
                              key={v}
                              onClick={() => insertarVariable(v)}
                              className="text-xs bg-white px-2 py-1 rounded mr-1 mb-1 hover:bg-cyan-100"
                            >
                              {v}
                            </button>
                          ))}
                        </div>
                      )}
                    <div>
                      <p className="text-xs font-semibold text-cyan-700 mb-1">Cliente:</p>
                      {variablesDisponibles.variables_cliente.map((v) => (
                        <button
                          key={v}
                          onClick={() => insertarVariable(v)}
                          className="text-xs bg-white px-2 py-1 rounded mr-1 mb-1 hover:bg-cyan-100"
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-cyan-700 mb-1">Reserva:</p>
                      {variablesDisponibles.variables_reserva.map((v) => (
                        <button
                          key={v}
                          onClick={() => insertarVariable(v)}
                          className="text-xs bg-white px-2 py-1 rounded mr-1 mb-1 hover:bg-cyan-100"
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs font-semibold text-cyan-700 mb-1">Datos:</p>
                      <div className="flex flex-wrap">
                        {Object.keys(variablesDisponibles.variables_datos).map((v) => (
                          <div key={v} className="flex items-center mr-1 mb-1 group">
                            <button
                              onClick={() => insertarVariable(v)}
                              className="text-xs bg-white px-2 py-1 rounded-l hover:bg-cyan-100 border border-r-0 border-cyan-200"
                            >
                              {`{${v}}`}
                            </button>
                            <button
                              onClick={() => eliminarVariableDato(v)}
                              className="text-xs bg-red-50 px-1 py-1 rounded-r hover:bg-red-100 border border-red-200 opacity-50 group-hover:opacity-100 transition-opacity"
                              title={`Eliminar {${v}}`}
                            >
                              <X className="w-3 h-3 text-red-500" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              )}

              {/* Paso 4: Editar Contenido */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label htmlFor="contenido" className="text-base font-semibold text-cyan-800">
                    4. Editar Contenido del Contrato
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => insertarTablaEnEditor(3, 3, false)}
                    className="text-xs"
                  >
                    ➕ Insertar Tabla (3x3)
                  </Button>
                </div>
                <div className="border rounded-lg">
                  <ReactQuill
                    ref={quillRefContrato}
                    theme="snow"
                    value={contenidoContrato}
                    onChange={setContenidoContrato}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Cargue una plantilla o escriba el contenido del contrato aquí..."
                    style={{ height: '400px', marginBottom: '50px' }}
                  />
                </div>
              </div>

              {/* Botones de acción */}
              <div className="flex gap-4 pt-4">
                <Button
                  onClick={generarContrato}
                  disabled={!clienteSeleccionado || !reservaSeleccionada || !contenidoContrato.trim() || generandoPDF}
                  className="bg-cyan-600 hover:bg-cyan-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {generandoPDF ? 'Generando...' : 'Generar Contrato y PDF'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setVistaActual('contratos')}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Crear/Editar Plantilla */}
        <Dialog open={modalPlantillaAbierto} onOpenChange={setModalPlantillaAbierto}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {plantillaEditando ? 'Editar Plantilla' : 'Nueva Plantilla'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="nombrePlantilla">Nombre de la Plantilla</Label>
                <Input
                  id="nombrePlantilla"
                  value={nombrePlantilla}
                  onChange={(e) => setNombrePlantilla(e.target.value)}
                  placeholder="Ej: Contrato de Arriendo Estándar"
                />
              </div>

              {/* Variables Disponibles */}
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                <h3 className="font-semibold text-cyan-800 mb-3 text-sm">
                  Variables disponibles - Haz click para insertar:
                </h3>
                <div className="space-y-3">
                  {/* Variables Generales */}
                  {variablesDisponibles.variables_generales && variablesDisponibles.variables_generales.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-cyan-700 mb-2">Generales:</p>
                      <div className="flex flex-wrap gap-2">
                        {variablesDisponibles.variables_generales.map((v) => (
                          <Button
                            key={v}
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => insertarVariableEnPlantilla(v)}
                            className="text-xs bg-white hover:bg-cyan-100 border-cyan-300"
                          >
                            {v}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Variables de Cliente */}
                  <div>
                    <p className="text-xs font-semibold text-cyan-700 mb-2">Cliente:</p>
                    <div className="flex flex-wrap gap-2">
                      {variablesDisponibles.variables_cliente.map((v) => (
                        <Button
                          key={v}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => insertarVariableEnPlantilla(v)}
                          className="text-xs bg-white hover:bg-cyan-100 border-cyan-300"
                        >
                          {v}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Variables de Reserva */}
                  <div>
                    <p className="text-xs font-semibold text-cyan-700 mb-2">Reserva:</p>
                    <div className="flex flex-wrap gap-2">
                      {variablesDisponibles.variables_reserva.map((v) => (
                        <Button
                          key={v}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => insertarVariableEnPlantilla(v)}
                          className="text-xs bg-white hover:bg-cyan-100 border-cyan-300"
                        >
                          {v}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Variables de Datos */}
                  {Object.keys(variablesDisponibles.variables_datos).length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-cyan-700 mb-2">Datos:</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.keys(variablesDisponibles.variables_datos).map((v) => (
                          <div key={v} className="flex items-center gap-0.5 group">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => insertarVariableEnPlantilla(v)}
                              className="text-xs bg-white hover:bg-cyan-100 border-cyan-300 rounded-r-none border-r-0"
                            >
                              {`{${v}}`}
                            </Button>
                            <button
                              type="button"
                              onClick={() => eliminarVariableDato(v)}
                              className="h-8 px-1.5 border border-l-0 border-red-300 bg-red-50 hover:bg-red-100 rounded-r-md transition-colors opacity-50 group-hover:opacity-100"
                              title={`Eliminar variable {${v}}`}
                            >
                              <X className="w-3 h-3 text-red-500" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">✕ Click para eliminar variable</p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <Label htmlFor="contenidoPlantilla">Contenido de la Plantilla</Label>
                    <p className="text-sm text-gray-500">
                      Haz click en las variables de arriba para insertarlas. Usa la barra de herramientas para dar formato.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => insertarTablaEnEditor(3, 3, true)}
                    className="text-xs"
                  >
                    ➕ Insertar Tabla (3x3)
                  </Button>
                </div>
                <div className="border rounded-lg">
                  <ReactQuill
                    ref={quillRefPlantilla}
                    theme="snow"
                    value={contenidoPlantilla}
                    onChange={setContenidoPlantilla}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Escriba el contenido de la plantilla aquí..."
                    style={{ height: '400px', marginBottom: '50px' }}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setModalPlantillaAbierto(false)}>
                  Cancelar
                </Button>
                <Button onClick={guardarPlantilla} className="bg-cyan-600 hover:bg-cyan-700">
                  <Save className="w-4 h-4 mr-2" />
                  Guardar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal: Visualizar Contrato */}
        <Dialog open={modalVisualizarAbierto} onOpenChange={setModalVisualizarAbierto}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Contrato - {contratoSeleccionado?.nombre_cliente}</DialogTitle>
            </DialogHeader>
            {contratoSeleccionado && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded">
                  <p className="text-sm text-gray-600">
                    <strong>Fecha de generación:</strong>{' '}
                    {contratoSeleccionado.fecha_generacion 
                      ? new Date(contratoSeleccionado.fecha_generacion).toLocaleDateString('es-CL')
                      : 'Sin fecha'}
                  </p>
                </div>
                <div 
                  className="prose prose-sm max-w-none border p-4 rounded bg-white"
                  dangerouslySetInnerHTML={{ __html: contratoSeleccionado.contenido_completo || contratoSeleccionado.contenido || '' }}
                />
                <div className="flex justify-end gap-2">
                  {contratoSeleccionado.pdf_url && (
                    <Button
                      onClick={() => {
                        setModalVisualizarAbierto(false);
                        descargarPDF(contratoSeleccionado);
                      }}
                      className="bg-cyan-600 hover:bg-cyan-700"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Ver PDF
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setModalVisualizarAbierto(false)}>
                    Cerrar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Alert Dialog: Confirmar Eliminar Plantilla */}
        <AlertDialog open={!!plantillaAEliminar} onOpenChange={() => setPlantillaAEliminar(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                ¿Eliminar plantilla?
              </AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer. Se eliminará la plantilla &quot;{plantillaAEliminar?.nombre}&quot;.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmarEliminarPlantilla}
                className="bg-red-600 hover:bg-red-700"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Alert Dialog: Confirmar Eliminar Contrato */}
        <AlertDialog open={!!contratoAEliminar} onOpenChange={() => setContratoAEliminar(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                ¿Eliminar contrato?
              </AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer. Se eliminará el contrato de &quot;{contratoAEliminar?.nombre_cliente}&quot;.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmarEliminarContrato}
                className="bg-red-600 hover:bg-red-700"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Modal: Vista Previa PDF */}
        <Dialog open={modalPDFPreviewAbierto} onOpenChange={setModalPDFPreviewAbierto}>
          <DialogContent className="max-w-5xl max-h-[95vh] p-0">
            <DialogHeader className="p-4 pb-2 border-b">
              <DialogTitle className="flex items-center justify-between">
                <span>Vista Previa - {contratoSeleccionado?.nombre_cliente}</span>
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 h-[70vh] bg-gray-100 relative">
              {pdfPreviewUrl ? (
                <>
                  <iframe
                    src={pdfPreviewUrl}
                    className="w-full h-full border-0"
                    title="Vista previa del contrato PDF"
                  />
                  <div className="absolute bottom-4 left-4 text-xs text-gray-500 bg-white/80 px-2 py-1 rounded">
                    Si el PDF no se visualiza, usa "Abrir en nueva pestaña"
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  No hay PDF disponible
                </div>
              )}
            </div>
            <div className="p-4 pt-2 border-t flex justify-between">
              <Button 
                variant="outline" 
                onClick={() => window.open(pdfPreviewUrl, '_blank')}
                className="text-gray-600"
              >
                <Eye className="w-4 h-4 mr-2" />
                Abrir en nueva pestaña
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setModalPDFPreviewAbierto(false)}>
                  Cerrar
                </Button>
                <Button onClick={descargarPDFDirecto} className="bg-cyan-600 hover:bg-cyan-700">
                  <Download className="w-4 h-4 mr-2" />
                  Descargar PDF
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Contratos;
